import React from 'react'
import { useSlider, useIsMobile } from './slider.hook'

type SliderLayout = 'grid' | 'list'

interface SliderProps<T> {
  items: T[]
  renderItem: (item: T, index: number) => React.ReactNode
  layout?: SliderLayout
  autoLoop?: boolean
  delay?: number
  className?: string
  itemsPerSlideMobile?: number
  itemsPerSlideDesktop?: number
  dotsClassName?: string
  onSlideChange?: (activeSlide: number) => void
}

export function Slider<T>({
  items,
  renderItem,
  layout = 'grid',
  autoLoop,
  delay,
  className = '',
  itemsPerSlideDesktop = 6,
  itemsPerSlideMobile = 6,
  dotsClassName = '',
  onSlideChange,
}: SliderProps<T>) {
  const isMobile = useIsMobile()
  const itemsPerSlide =
    layout === 'list'
      ? isMobile
        ? 1
        : 2
      : isMobile
        ? itemsPerSlideMobile
        : itemsPerSlideDesktop

  const {
    slides,
    currentSlide,
    activeSlide,
    totalSlides,
    goToSlide,
    animated,
    handleTransitionEnd,
    handleTouchStart,
    handleTouchEnd,
  } = useSlider(items, { autoLoop, delay, itemsPerSlide, onSlideChange })

  if (items.length === 0) return undefined

  const slideContent = (slide: T[], slideIndex: number, isClone = false) => (
    <div
      key={isClone ? 'clone' : slideIndex}
      className="w-full shrink-0"
      aria-hidden={isClone || undefined}
    >
      {layout === 'list' ? (
        <div className="flex flex-col gap-4">
          {slide.map((item, itemIndex) =>
            renderItem(
              item,
              isClone ? itemIndex : slideIndex * itemsPerSlide + itemIndex,
            ),
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-6 md:gap-8">
          {slide.map((item, itemIndex) =>
            renderItem(
              item,
              isClone ? itemIndex : slideIndex * itemsPerSlide + itemIndex,
            ),
          )}
        </div>
      )}
    </div>
  )

  const showDots = totalSlides > 1 && !(layout === 'list' && isMobile)

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="my-auto overflow-hidden">
        <div
          className="flex"
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: animated
              ? `transform var(--default-transition-duration) var(--default-transition-timing-function)`
              : 'none',
          }}
          onTransitionEnd={handleTransitionEnd}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {slides.map((slide, slideIndex) => slideContent(slide, slideIndex))}

          {/* Clone of the first slide — lets the loop animate forward instead of snapping back */}
          {totalSlides > 1 && slides[0] && slideContent(slides[0], 0, true)}
        </div>
      </div>

      {showDots && (
        <div className="flex items-center justify-center gap-2">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={[
                'size-4 rounded-full border transition-colors',
                dotsClassName,
                i === activeSlide
                  ? 'border-indigo-base border-4 bg-indigo-50'
                  : 'border-indigo-base bg-indigo-50',
              ].join(' ')}
            />
          ))}
        </div>
      )}
    </div>
  )
}
